function buildAttendeeRowValidationResult({ nameOk, ageOk, telOk }) {
  const invalid = {
    name: !nameOk,
    age: !ageOk,
    tel: !telOk,
  };
  const field = invalid.name ? 'name' : invalid.age ? 'age' : invalid.tel ? 'tel' : '';
  return {
    ok: nameOk && ageOk && telOk,
    invalid,
    field,
  };
}

function createValidateAttendeeRow(deps = {}) {
  const {
    getAttendeeInputs,
    validateAttendeeNameField,
    validateAttendeeAgeField,
    validateAttendeeTelField,
  } = deps;

  return function validateAttendeeRow(row, { showErrors = false } = {}) {
    const { nameInput, ageInput, telInput } = getAttendeeInputs(row);
    const nameOk = validateAttendeeNameField(nameInput, { showErrors });
    const ageOk = validateAttendeeAgeField(ageInput, { showErrors });
    const telOk = validateAttendeeTelField(telInput, { showErrors });
    return buildAttendeeRowValidationResult({ nameOk, ageOk, telOk });
  };
}

function cloneAttendeeTemplateNode(attendeeTemplate, options = {}) {
  const { consoleObj = console } = options;
  const templateRoot = attendeeTemplate?.content?.firstElementChild || null;
  if (!templateRoot || typeof templateRoot.cloneNode !== 'function') {
    if (consoleObj && typeof consoleObj.warn === 'function') {
      consoleObj.warn('bulk-order attendee template root is missing');
    }
    return null;
  }
  return templateRoot.cloneNode(true);
}

function resolveCartUrl(routes) {
  return routes && routes.cart_url ? routes.cart_url : '/cart';
}

function hasBulkItemBaseControls({ checkbox, controls, qtyInput }) {
  return Boolean(checkbox && controls && qtyInput);
}

function hasBulkTourFormDom({
  birthdateInput,
  birthdateYear,
  birthdateMonth,
  birthdateDay,
  attendeesWrap,
  attendeesGrid,
  attendeeTemplate,
  consentMain,
  consentMinorWrap,
  consentMinor,
}) {
  return Boolean(
    birthdateInput &&
    birthdateYear &&
    birthdateMonth &&
    birthdateDay &&
    attendeesWrap &&
    attendeesGrid &&
    attendeeTemplate &&
    consentMain &&
    consentMinorWrap &&
    consentMinor
  );
}

function disableInvalidBulkItemSelection({ checkbox, toggleSelected, updateSubmitState }) {
  if (checkbox) {
    checkbox.checked = false;
  }
  if (typeof toggleSelected === 'function') {
    toggleSelected(false);
  }
  if (checkbox) {
    checkbox.disabled = true;
  }
  if (typeof updateSubmitState === 'function') {
    updateSubmitState();
  }
}

function applyBulkItemPresaleState({
  item,
  checkbox,
  statusEl,
  countdownEl,
  locked,
  countdownText = '',
  toggleSelected,
  updateSubmitState,
}) {
  if (item && item.dataset) {
    item.dataset.presaleLocked = locked ? 'true' : 'false';
  }
  if (item && item.classList && typeof item.classList.toggle === 'function') {
    item.classList.toggle('is-presale-locked', locked);
  }
  if (checkbox) {
    if (locked) {
      checkbox.checked = false;
      if (typeof toggleSelected === 'function') {
        toggleSelected(false);
      }
    }
    checkbox.disabled = locked;
  }
  if (statusEl) {
    statusEl.hidden = !locked;
  }
  if (countdownEl) {
    countdownEl.textContent = locked ? countdownText : '';
  }
  if (typeof updateSubmitState === 'function') {
    updateSubmitState();
  }
}

function redirectToCart(win) {
  if (!win || !win.location) return;
  const targetUrl = resolveCartUrl(win.routes);
  if (typeof window !== 'undefined' && win === window) {
    try {
      window.location = targetUrl;
      return;
    } catch (_err) {
      // Fall through to the existing navigation fallbacks.
    }
  }
  if (win.Shopify && win.Shopify.designMode && win.document && typeof win.document.createElement === 'function') {
    try {
      const anchor = win.document.createElement('a');
      if (anchor && typeof anchor.click === 'function') {
        anchor.href = targetUrl;
        if (win.document.body && typeof win.document.body.appendChild === 'function') {
          win.document.body.appendChild(anchor);
        }
        anchor.click();
        return;
      }
    } catch (_err) {
      // Fall through to the existing navigation fallbacks.
    }
  }
  if (win.document && win.document.body && typeof win.document.createElement === 'function') {
    try {
      const form = win.document.createElement('form');
      if (form && typeof form.submit === 'function') {
        form.method = 'GET';
        form.action = targetUrl;
        win.document.body.appendChild(form);
        form.submit();
        const deferCleanup = typeof win.setTimeout === 'function'
          ? win.setTimeout.bind(win)
          : (typeof setTimeout === 'function' ? setTimeout : null);
        if (deferCleanup) {
          deferCleanup(() => {
            if (typeof form.remove === 'function') {
              form.remove();
            } else if (typeof win.document.body.removeChild === 'function') {
              win.document.body.removeChild(form);
            }
          }, 0);
        }
        return;
      }
    } catch (_err) {
      // Fall through to the existing navigation fallbacks.
    }
  }
  if (typeof win.location.assign === 'function') {
    win.location.assign(targetUrl);
    return;
  }
  if (win.document && typeof win.document.createElement === 'function') {
    const anchor = win.document.createElement('a');
    if (anchor) {
      anchor.href = targetUrl;
      if (anchor.href) {
        win.location.href = anchor.href;
        return;
      }
    }
  }
  win.location.href = targetUrl;
}

async function postBulkOrderItemsWithTimeout(options = {}) {
  const {
    fetchImpl,
    url,
    payloadItems,
    setError,
    onSuccess,
    timeoutMs = 15000,
    AbortControllerImpl = typeof AbortController === 'function' ? AbortController : null,
    setTimeoutFn = typeof setTimeout === 'function' ? setTimeout : () => null,
    clearTimeoutFn = typeof clearTimeout === 'function' ? clearTimeout : () => {},
    consoleObj = console,
  } = options;

  const controller = typeof AbortControllerImpl === 'function' ? new AbortControllerImpl() : null;
  const timeoutId = controller ? setTimeoutFn(() => controller.abort(), timeoutMs) : null;

  try {
    const res = await fetchImpl(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: payloadItems }),
      signal: controller ? controller.signal : undefined,
    });
    const data = await res.json();
    if (data.status) {
      if (typeof setError === 'function') setError(data.description || 'カート追加に失敗しました。');
      return { ok: false, timedOut: false, res, data };
    }
    if (typeof onSuccess === 'function') await onSuccess(data, res);
    return { ok: true, timedOut: false, res, data };
  } catch (err) {
    if (err && err.name === 'AbortError') {
      if (typeof setError === 'function') setError('通信がタイムアウトしました。時間をおいて再度お試しください。');
      return { ok: false, timedOut: true, error: err };
    }
    if (consoleObj && typeof consoleObj.error === 'function') {
      consoleObj.error(err);
    }
    if (typeof setError === 'function') setError('通信エラーが発生しました。時間をおいて再度お試しください。');
    return { ok: false, timedOut: false, error: err };
  } finally {
    if (timeoutId !== null) clearTimeoutFn(timeoutId);
  }
}

(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const bulkOrderModule = window.MFBulkOrder || {};
  const createBulkItemPresaleController = bulkOrderModule.createBulkItemPresaleController;
  const createBulkOrderValidateSelectedItem = bulkOrderModule.createBulkOrderValidateSelectedItem;
  const createPresaleGuardedValidateSelectedItem = bulkOrderModule.createPresaleGuardedValidateSelectedItem;
  if (
    typeof createBulkItemPresaleController !== 'function' ||
    typeof createBulkOrderValidateSelectedItem !== 'function' ||
    typeof createPresaleGuardedValidateSelectedItem !== 'function'
  ) {
    console.error('bulk-order.js must be loaded before bulk-order-browser.js');
    return;
  }

  const presaleHelpers = window.MFPresaleHelpers || {};
  const getPresaleState = presaleHelpers.getPresaleState;
  const getPresaleCountdownState = presaleHelpers.getPresaleCountdownState;
  const formatPresaleRemaining = presaleHelpers.formatPresaleRemaining;
  if (
    typeof getPresaleState !== 'function' ||
    typeof getPresaleCountdownState !== 'function' ||
    typeof formatPresaleRemaining !== 'function'
  ) {
    console.error('presale-helpers.js must be loaded before bulk-order-browser.js');
    return;
  }

  const tourFormHelpers = window.MFTourFormHelpers || {};
  const parseDateParts = typeof tourFormHelpers.parseDateParts === 'function'
    ? tourFormHelpers.parseDateParts
    : (raw) => {
        const match = String(raw || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!match) return null;
        return {
          year: parseInt(match[1], 10),
          month: parseInt(match[2], 10),
          day: parseInt(match[3], 10),
        };
      };
  const calculateAgeOnDate = typeof tourFormHelpers.calculateAgeOnDate === 'function'
    ? tourFormHelpers.calculateAgeOnDate
    : (birthdateIso, referenceIso) => {
        const birth = parseDateParts(birthdateIso);
        const ref = parseDateParts(referenceIso);
        if (!birth || !ref) return null;
        let age = ref.year - birth.year;
        if (ref.month < birth.month || (ref.month === birth.month && ref.day < birth.day)) age -= 1;
        return age >= 0 ? age : null;
      };
  const isMinorAge = typeof tourFormHelpers.isMinorAge === 'function'
    ? tourFormHelpers.isMinorAge
    : (age) => Number.isFinite(age) && age >= 0 && age < 18;
  const getTodayIsoDate = typeof tourFormHelpers.getTodayIsoDate === 'function'
    ? tourFormHelpers.getTodayIsoDate
    : () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
  const normalizePhoneForValidation = typeof tourFormHelpers.normalizePhoneForValidation === 'function'
    ? tourFormHelpers.normalizePhoneForValidation
    : (raw) => {
        if (!raw) return '';
        const v = String(raw)
          .trim()
          .replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 65248))
          .replace(/[‐‑‒–—―ー−]/g, '-')
          .replace(/[()\s-]/g, '');
        if (v.startsWith('+81')) {
          return `0${v.slice(3)}`;
        }
        return v;
      };
  const isValidPhoneNumber = typeof tourFormHelpers.isValidPhoneNumber === 'function'
    ? tourFormHelpers.isValidPhoneNumber
    : (raw) => {
        const normalized = normalizePhoneForValidation(raw);
        if (!normalized) return true;
        return /^0\d{9,10}$/.test(normalized);
      };
  const cartAddTimeoutMs = 15000;
  const resolveAgeReferenceDate = typeof tourFormHelpers.resolveAgeReferenceDate === 'function'
    ? tourFormHelpers.resolveAgeReferenceDate
    : (raw, options = {}) => {
        const normalized = parseDateParts(raw);
        if (normalized) {
          return {
            value: `${String(normalized.year).padStart(4, '0')}-${String(normalized.month).padStart(2, '0')}-${String(normalized.day).padStart(2, '0')}`,
            source: 'configured',
          };
        }
        if (options.fallbackToToday) {
          return { value: getTodayIsoDate(), source: 'today' };
        }
        return { value: '', source: 'missing' };
      };

  function byId(id) {
    return document.getElementById(id);
  }

  function toNumber(val) {
    const n = parseInt(val, 10);
    return Number.isFinite(n) ? n : null;
  }

  function parseVariantId(val) {
    return String(val || '').trim();
  }

  function isValidVariantId(variantId) {
    return /^\d+$/.test(variantId);
  }

  function normalizeQuantity(value, min, max, step) {
    let v = Number.isFinite(value) ? value : min;
    if (!Number.isFinite(v)) v = 1;
    if (v < min) v = min;
    if (Number.isFinite(max) && v > max) v = max;
    if (step > 1) {
      const offset = (v - min) % step;
      if (offset !== 0) v = v - offset;
    }
    return v;
  }

  function hasMinor(ages) {
    return ages.some((age) => Number.isFinite(age) && age >= 0 && age < 18);
  }

  function getFieldErrorEl(inputEl) {
    if (!inputEl) return null;
    const wrap = inputEl.parentElement;
    if (!wrap) return null;
    return wrap.querySelector('[data-field-error]');
  }

  function clearFieldError(inputEl) {
    if (!inputEl) return;
    inputEl.removeAttribute('aria-invalid');
    const err = getFieldErrorEl(inputEl);
    if (!err) return;
    err.textContent = '';
    err.hidden = true;
  }

  function clearFieldErrors(scopeEl) {
    if (!scopeEl) return;
    scopeEl.querySelectorAll('[data-field]').forEach((inputEl) => {
      clearFieldError(inputEl);
    });
  }

  function showFieldError(inputEl, message) {
    if (!inputEl) return false;
    const err = getFieldErrorEl(inputEl);
    if (!err) return false;
    inputEl.setAttribute('aria-invalid', 'true');
    err.textContent = message;
    err.hidden = false;
    return true;
  }

  function getAttendeeInputs(row) {
    if (!row) {
      return {
        nameInput: null,
        ageInput: null,
        genderInput: null,
        telInput: null,
      };
    }
    return {
      nameInput: row.querySelector('[data-field="name"]'),
      ageInput: row.querySelector('[data-field="age"]'),
      genderInput: row.querySelector('[data-field="gender"]'),
      telInput: row.querySelector('[data-field="tel"]'),
    };
  }

  function initSection(root) {
    const sectionId = root.dataset.sectionId;
    const form = root.querySelector('[data-bulk-order-form]');
    const submitBtn = root.querySelector('[data-bulk-submit]');
    const errorBox = root.querySelector('[data-bulk-error]');
    const items = Array.from(root.querySelectorAll('[data-bulk-item]'));

    function getSelectedItems() {
      return items.filter((it) => it.querySelector('[data-bulk-select]')?.checked);
    }

    function setError(msg) {
      if (!errorBox) return;
      if (msg) {
        errorBox.textContent = msg;
        errorBox.hidden = false;
      } else {
        errorBox.textContent = '';
        errorBox.hidden = true;
      }
    }

    function parseVariants(productId) {
      const jsonEl = byId(`mf-variants-${sectionId}-${productId}`);
      if (!jsonEl) return [];
      try {
        return JSON.parse(jsonEl.textContent || '[]');
      } catch {
        return [];
      }
    }

    function setupItem(item) {
      const productId = item.dataset.productId;
      const productTitle = item.dataset.productTitle || '';
      const isTour = item.dataset.isTour === 'true';
      const saleStartUnix = String(item.dataset.saleStartUnix || '').trim();
      const configuredAgeReferenceDate = String(item.dataset.ageReferenceDate || '').trim();
      const ageReferenceResolution = resolveAgeReferenceDate(configuredAgeReferenceDate, { fallbackToToday: true });
      const ageReferenceDate = String(ageReferenceResolution.value || '').trim();
      const defaultVariantId = parseVariantId(item.dataset.variantId);
      const checkbox = item.querySelector('[data-bulk-select]');
      const controls = item.querySelector('[data-bulk-controls]');
      const qtyInput = item.querySelector('[data-qty-input]');
      const priceEl = item.querySelector('[data-price]');
      const noteEl = item.querySelector('[data-limit-note]');
      const warningEl = item.querySelector('[data-limit-warning]');
      const birthdateInput = item.querySelector('[data-birthdate-input]');
      const birthdateYear = item.querySelector('[data-birthdate-year]');
      const birthdateMonth = item.querySelector('[data-birthdate-month]');
      const birthdateDay = item.querySelector('[data-birthdate-day]');
      const birthdateErrorEl = item.querySelector('[data-birthdate-error]');
      const birthdateReferenceNote = item.querySelector('[data-birthdate-reference-note]');
      const attendeesWrap = item.querySelector('[data-attendees]');
      const attendeesGrid = item.querySelector('[data-attendees-grid]');
      const attendeeTemplate = item.querySelector('[data-attendee-template]');
      const consentMain = item.querySelector('[data-consent-main]');
      const consentMinorWrap = item.querySelector('[data-consent-minor-wrap]');
      const consentMinor = item.querySelector('[data-consent-minor]');
      const presaleStatus = item.querySelector('[data-bulk-presale-status]');
      const presaleCountdown = item.querySelector('[data-bulk-presale-countdown]');
      const variants = parseVariants(productId);

      function getVariant() {
        return variants.find((v) => String(v.id) === defaultVariantId) || variants[0] || null;
      }

      function getInventoryMax(variant) {
        if (!variant) return null;
        const qty = toNumber(variant.inventory_quantity);
        if (!Number.isFinite(qty)) return null;
        return Math.max(0, qty);
      }

      function updatePrice(variant) {
        if (!priceEl || !variant) return;
        priceEl.textContent = variant.price_money || '';
      }

      function computeMaxQuantity(variant) {
        let max = Number.isFinite(variant?.quantity_rule?.max) ? variant.quantity_rule.max : null;
        const inventoryMax = getInventoryMax(variant);
        if (Number.isFinite(inventoryMax)) {
          max = Number.isFinite(max) ? Math.min(max, inventoryMax) : inventoryMax;
        }
        return Number.isFinite(max) ? max : null;
      }

      function setBirthdateError(message) {
        if (!birthdateErrorEl) return;
        birthdateErrorEl.textContent = message || '';
        birthdateErrorEl.hidden = !message;
        [birthdateYear, birthdateMonth, birthdateDay].forEach((el) => {
          if (!el) return;
          if (message) {
            el.setAttribute('aria-invalid', 'true');
          } else {
            el.removeAttribute('aria-invalid');
          }
        });
      }

      function getApplicantBirthdateIso() {
        return birthdateInput ? String(birthdateInput.value || '').trim() : '';
      }

      function updateBirthdateHidden() {
        if (!birthdateInput) return;
        const y = birthdateYear ? String(birthdateYear.value || '').trim() : '';
        const m = birthdateMonth ? String(birthdateMonth.value || '').trim() : '';
        const d = birthdateDay ? String(birthdateDay.value || '').trim() : '';
        birthdateInput.value = y && m && d
          ? `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
          : '';
        setBirthdateError('');
      }

      function updateBirthdateDays() {
        if (!birthdateDay) return;
        const y = toNumber(birthdateYear?.value);
        const m = toNumber(birthdateMonth?.value);
        const prevDay = birthdateDay.value;
        let maxDay = 31;
        if (Number.isFinite(m) && m >= 1 && m <= 12) {
          const yearForCalc = Number.isFinite(y) ? y : 2000;
          maxDay = new Date(yearForCalc, m, 0).getDate();
        }
        const currentMax = birthdateDay.querySelectorAll('option').length - 1;
        if (currentMax < maxDay) {
          for (let d = currentMax + 1; d <= maxDay; d++) {
            const option = document.createElement('option');
            option.value = String(d);
            option.textContent = `${d}日`;
            birthdateDay.appendChild(option);
          }
        } else if (currentMax > maxDay) {
          for (let d = currentMax; d > maxDay; d--) {
            birthdateDay.removeChild(birthdateDay.lastChild);
          }
        }
        if (toNumber(prevDay) > maxDay) birthdateDay.value = String(maxDay);
        updateBirthdateHidden();
      }

      function populateBirthdateYears() {
        if (!birthdateYear) return;
        if (birthdateYear.options.length > 1) return;
        const currentYear = new Date().getFullYear();
        for (let y = currentYear; y >= 1920; y--) {
          const option = document.createElement('option');
          option.value = String(y);
          option.textContent = `${y}年`;
          birthdateYear.appendChild(option);
        }
      }

      function getApplicantAge() {
        const birthdateIso = getApplicantBirthdateIso();
        if (!birthdateIso || !ageReferenceDate) return null;
        return calculateAgeOnDate(birthdateIso, ageReferenceDate);
      }

      function getAttendeeAges() {
        if (!attendeesGrid) return [];
        return Array.from(attendeesGrid.querySelectorAll('[data-field="age"]'))
          .map((el) => toNumber(el.value))
          .filter((age) => Number.isFinite(age) && age >= 0);
      }

      function shouldShowMinorConsent() {
        const applicantAge = getApplicantAge();
        if (isMinorAge(applicantAge)) return true;
        return getAttendeeAges().some(isMinorAge);
      }

      function validateBirthdateInputField() {
        if (!birthdateInput) return true;
        const birthdateIso = getApplicantBirthdateIso();
        if (!birthdateIso) return true;
        if (getApplicantAge() === null) {
          setBirthdateError('生年月日を正しく入力してください。');
          return false;
        }
        setBirthdateError('');
        return true;
      }

      function validateAttendeeNameField(inputEl, { showErrors = false } = {}) {
        const value = inputEl?.value?.trim() || '';
        if (value) {
          if (showErrors) clearFieldError(inputEl);
          return true;
        }
        if (showErrors) showFieldError(inputEl, '氏名を入力してください。');
        return false;
      }

      function validateAttendeeAgeField(inputEl, { showErrors = false } = {}) {
        const raw = inputEl?.value?.trim() || '';
        if (!raw) {
          if (showErrors) showFieldError(inputEl, '年齢を入力してください。');
          return false;
        }
        const age = toNumber(raw);
        if (Number.isFinite(age) && age >= 0) {
          if (showErrors) clearFieldError(inputEl);
          return true;
        }
        if (showErrors) showFieldError(inputEl, '年齢を正しく入力してください。');
        return false;
      }

      function validateAttendeeTelField(inputEl, { showErrors = false } = {}) {
        const tel = inputEl?.value?.trim() || '';
        if (!tel) {
          if (showErrors) clearFieldError(inputEl);
          return true;
        }
        if (isValidPhoneNumber(tel)) {
          if (showErrors) clearFieldError(inputEl);
          return true;
        }
        if (showErrors) showFieldError(inputEl, '電話番号を正しく入力してください（例: 090-1234-5678）。');
        return false;
      }

      function validateAttendeeField(inputEl, { showErrors = false } = {}) {
        if (!inputEl) return true;
        const fieldType = inputEl.dataset.field;
        if (fieldType === 'name') return validateAttendeeNameField(inputEl, { showErrors });
        if (fieldType === 'age') return validateAttendeeAgeField(inputEl, { showErrors });
        if (fieldType === 'tel') return validateAttendeeTelField(inputEl, { showErrors });
        if (showErrors) clearFieldError(inputEl);
        return true;
      }

      const validateAttendeeRow = createValidateAttendeeRow({
        getAttendeeInputs,
        validateAttendeeNameField,
        validateAttendeeAgeField,
        validateAttendeeTelField,
      });

      function getSelectedQuantity() {
        return qtyInput ? toNumber(qtyInput.value) : 1;
      }

      function getAttendeeRows() {
        return attendeesGrid ? Array.from(attendeesGrid.querySelectorAll('[data-attendee-row]')) : [];
      }

      function createSimpleValidateSelectedItem() {
        return function validateSelectedItem() {
          const productTitle = item.dataset.productTitle || '';
          const variantId = parseVariantId(item.dataset.variantId);
          const quantity = getSelectedQuantity();

          if (!isValidVariantId(variantId)) {
            return { ok: false, message: `バリアントが取得できません：${productTitle}` };
          }
          if (!Number.isFinite(quantity) || quantity < 1) {
            return { ok: false, message: `数量が正しくありません：${productTitle}` };
          }

          return { ok: true, variantId, quantity, properties: {} };
        };
      }

      function createInitializationFailureValidateSelectedItem() {
        return function validateSelectedItem() {
          return {
            ok: false,
            message: `ツアー入力フォームの初期化に失敗しました：${item.dataset.productTitle || ''}`,
          };
        };
      }

      function assignItemState(validateSelectedItem) {
        item.__bulkTourState = {
          getApplicantBirthdateIso,
          getApplicantAge,
          setBirthdateError,
          validateSelectedItem,
        };
      }

      const hasBaseControls = hasBulkItemBaseControls({ checkbox, controls, qtyInput });
      if (!hasBaseControls) {
        assignItemState(createSimpleValidateSelectedItem());
        return;
      }

      const hasTourForm = !isTour || hasBulkTourFormDom({
        birthdateInput,
        birthdateYear,
        birthdateMonth,
        birthdateDay,
        attendeesWrap,
        attendeesGrid,
        attendeeTemplate,
        consentMain,
        consentMinorWrap,
        consentMinor,
      });

      const baseValidateSelectedItem = !isTour
        ? createSimpleValidateSelectedItem()
        : hasTourForm
          ? createBulkOrderValidateSelectedItem({
              parseVariantId,
              isValidVariantId,
              getSelectedQuantity,
              getApplicantBirthdateIso,
              validateBirthdateInputField,
              setBirthdateError,
              consentMain,
              getApplicantAge,
              isMinorAge,
              getAttendeeRows,
              validateAttendeeRow,
              getAttendeeInputs,
              isValidPhoneNumber,
              hasMinor,
              consentMinorWrap,
              consentMinor,
              item,
              isTour,
              birthdateYear,
              toNumber,
            })
          : createInitializationFailureValidateSelectedItem();
      const validateSelectedItem = createPresaleGuardedValidateSelectedItem({
        validateSelectedItem: baseValidateSelectedItem,
        getPresaleState,
        saleStartUnix,
        getNowUnix: () => Math.floor(Date.now() / 1000),
        productTitle,
      });

      if (!hasTourForm) {
        assignItemState(validateSelectedItem);
        disableInvalidBulkItemSelection({
          checkbox,
          toggleSelected,
          updateSubmitState,
        });
        return;
      }

      function applyVariantRules() {
        const variant = getVariant();
        if (!variant || !qtyInput) return;

        const min = Number.isFinite(variant.quantity_rule?.min) ? variant.quantity_rule.min : 1;
        const step = Number.isFinite(variant.quantity_rule?.increment) ? variant.quantity_rule.increment : 1;
        const max = computeMaxQuantity(variant);

        qtyInput.min = String(min);
        qtyInput.step = String(step);
        if (Number.isFinite(max)) {
          qtyInput.max = String(max);
        } else {
          qtyInput.removeAttribute('max');
        }

        const current = toNumber(qtyInput.value);
        const normalized = normalizeQuantity(current, min, max, step);
        qtyInput.value = String(normalized);

        if (noteEl) {
          if (Number.isFinite(max)) {
            noteEl.textContent = `最大人数（代表者含む）: ${max}名 / 同行者の上限: ${Math.max(0, max - 1)}名`;
          } else {
            noteEl.textContent = '';
          }
        }

        updatePrice(variant);
        renderAttendees();
      }

      function snapshotAttendees() {
        if (!attendeesGrid) return [];
        const rows = attendeesGrid.querySelectorAll('[data-attendee-row]');
        const data = [];
        rows.forEach((row, i) => {
          const { nameInput, ageInput, genderInput, telInput } = getAttendeeInputs(row);
          data[i] = {
            name: nameInput ? nameInput.value : '',
            age: ageInput ? ageInput.value : '',
            gender: genderInput ? genderInput.value : '',
            tel: telInput ? telInput.value : '',
          };
        });
        return data;
      }

      function attendeeCount() {
        if (!qtyInput) return 0;
        const q = toNumber(qtyInput.value) || 1;
        return Math.max(0, q - 1);
      }

      function renderAttendees() {
        if (!attendeesWrap || !attendeesGrid || !attendeeTemplate) return;
        if (!isTour) {
          attendeesWrap.hidden = true;
          return;
        }
        const count = attendeeCount();
        const prev = snapshotAttendees();
        attendeesGrid.innerHTML = '';
        for (let i = 0; i < count; i++) {
          const node = cloneAttendeeTemplateNode(attendeeTemplate, { consoleObj: console });
          if (!node) break;
          const idx = i + 1;
          const idBase = `mf-attendee-${productId}-${idx}`;
          const nameLabel = node.querySelector('[data-label="name"]');
          const ageLabel = node.querySelector('[data-label="age"]');
          const genderLabel = node.querySelector('[data-label="gender"]');
          const telLabel = node.querySelector('[data-label="tel"]');
          const { nameInput, ageInput, genderInput, telInput } = getAttendeeInputs(node);

          if (nameInput) nameInput.id = `${idBase}-name`;
          if (ageInput) ageInput.id = `${idBase}-age`;
          if (genderInput) genderInput.id = `${idBase}-gender`;
          if (telInput) telInput.id = `${idBase}-tel`;

          if (nameLabel) {
            nameLabel.textContent = `参加者${idx}：氏名（必須）`;
            if (nameInput) nameLabel.setAttribute('for', nameInput.id);
          }
          if (ageLabel) {
            ageLabel.textContent = `参加者${idx}：年齢（必須）`;
            if (ageInput) ageLabel.setAttribute('for', ageInput.id);
          }
          if (genderLabel) {
            genderLabel.textContent = `参加者${idx}：性別`;
            if (genderInput) genderLabel.setAttribute('for', genderInput.id);
          }
          if (telLabel) {
            telLabel.textContent = `参加者${idx}：電話（例: 090-1234-5678）`;
            if (telInput) telLabel.setAttribute('for', telInput.id);
          }
          if (telInput) telInput.placeholder = '090-1234-5678';

          const errors = node.querySelectorAll('[data-field-error]');
          if (errors[0] && nameInput) {
            errors[0].id = `${idBase}-name-error`;
            nameInput.setAttribute('aria-describedby', errors[0].id);
          }
          if (errors[1] && ageInput) {
            errors[1].id = `${idBase}-age-error`;
            ageInput.setAttribute('aria-describedby', errors[1].id);
          }
          if (errors[2] && genderInput) {
            errors[2].id = `${idBase}-gender-error`;
            genderInput.setAttribute('aria-describedby', errors[2].id);
          }
          if (errors[3] && telInput) {
            errors[3].id = `${idBase}-tel-error`;
            telInput.setAttribute('aria-describedby', errors[3].id);
          }

          const prevData = prev[i] || {};
          if (nameInput && prevData.name) nameInput.value = prevData.name;
          if (ageInput && prevData.age) ageInput.value = prevData.age;
          if (genderInput && prevData.gender) genderInput.value = prevData.gender;
          if (telInput && prevData.tel) telInput.value = prevData.tel;

          attendeesGrid.appendChild(node);
        }
        attendeesWrap.hidden = count === 0;
        updateMinorConsentVisibility();
      }

      function updateMinorConsentVisibility() {
        if (!consentMinorWrap) return;
        const show = shouldShowMinorConsent();
        consentMinorWrap.hidden = !show;
        if (!show && consentMinor) consentMinor.checked = false;
        updateSubmitState();
      }

      function updateSubmitState() {
        if (!submitBtn) return;
        const selectedItems = getSelectedItems();
        if (selectedItems.length === 0) {
          submitBtn.disabled = true;
          return;
        }
        for (const it of selectedItems) {
          const validation = it.__bulkTourState?.validateSelectedItem
            ? it.__bulkTourState.validateSelectedItem()
            : { ok: false };
          if (!validation.ok) {
            submitBtn.disabled = true;
            return;
          }
        }
        submitBtn.disabled = false;
      }

      function showLimitWarning(max) {
        if (!warningEl) return;
        warningEl.hidden = false;
        warningEl.textContent = `最大人数（代表者含む）: ${max}名までご予約いただけます。`;
      }

      function hideLimitWarning() {
        if (!warningEl) return;
        warningEl.hidden = true;
        warningEl.textContent = '';
      }

      function syncQuantity() {
        const variant = getVariant();
        if (!variant || !qtyInput) return;
        const min = Number.isFinite(variant.quantity_rule?.min) ? variant.quantity_rule.min : 1;
        const step = Number.isFinite(variant.quantity_rule?.increment) ? variant.quantity_rule.increment : 1;
        const max = computeMaxQuantity(variant);
        const raw = toNumber(qtyInput.value);
        const normalized = normalizeQuantity(raw, min, max, step);
        qtyInput.value = String(normalized);

        if (Number.isFinite(max) && raw && raw > max) {
          showLimitWarning(max);
        } else {
          hideLimitWarning();
        }

        renderAttendees();
        updateSubmitState();
      }

      function toggleSelected(on) {
        if (!controls) return;
        controls.hidden = !on;
        controls.querySelectorAll('input, select').forEach((el) => {
          if (el === checkbox) return;
          el.disabled = !on;
        });
        if (on) {
          applyVariantRules();
        }
        updateSubmitState();
      }

      const presaleController = createBulkItemPresaleController({
        saleStartUnix,
        getNowMs: () => Date.now(),
        getPresaleCountdownState,
        formatPresaleRemaining,
        applyPresaleState: applyBulkItemPresaleState,
        applyPresaleStateOptions: {
          item,
          checkbox,
          statusEl: presaleStatus,
          countdownEl: presaleCountdown,
          toggleSelected,
          updateSubmitState,
        },
        setIntervalFn: (callback, ms) => window.setInterval(callback, ms),
        clearIntervalFn: (id) => window.clearInterval(id),
      });

      if (qtyInput) {
        ['input', 'change'].forEach((evt) => qtyInput.addEventListener(evt, syncQuantity));
      }

      if (attendeesGrid) {
        const onAttendeeInput = (e) => {
          if (e.target && e.target.matches('[data-field]')) {
            validateAttendeeField(e.target, { showErrors: true });
          }
          updateMinorConsentVisibility();
        };
        attendeesGrid.addEventListener('input', onAttendeeInput, true);
        attendeesGrid.addEventListener('change', onAttendeeInput, true);
        attendeesGrid.addEventListener('focusout', (e) => {
          if (e.target && e.target.matches('[data-field]')) {
            validateAttendeeField(e.target, { showErrors: true });
            updateMinorConsentVisibility();
          }
        }, true);
      }

      if (birthdateInput) {
        populateBirthdateYears();
        if (birthdateReferenceNote) {
          birthdateReferenceNote.textContent = ageReferenceResolution.source === 'configured'
            ? `年齢判定基準日: ${ageReferenceDate}`
            : `年齢判定基準日: ${ageReferenceDate}（ツアー日未設定のため本日基準）`;
          birthdateReferenceNote.hidden = false;
        }
        if (birthdateYear) {
          birthdateYear.addEventListener('change', () => {
            updateBirthdateDays();
            validateBirthdateInputField();
            updateMinorConsentVisibility();
          });
        }
        if (birthdateMonth) {
          birthdateMonth.addEventListener('change', () => {
            updateBirthdateDays();
            validateBirthdateInputField();
            updateMinorConsentVisibility();
          });
        }
        if (birthdateDay) {
          birthdateDay.addEventListener('change', () => {
            updateBirthdateHidden();
            validateBirthdateInputField();
            updateMinorConsentVisibility();
          });
        }
        [birthdateYear, birthdateMonth, birthdateDay].forEach((el) => {
          if (!el) return;
          el.addEventListener('focusout', () => {
            validateBirthdateInputField();
            updateMinorConsentVisibility();
          });
        });
        updateBirthdateDays();
      }

      if (consentMain) consentMain.addEventListener('change', updateSubmitState, true);
      if (consentMinor) consentMinor.addEventListener('change', updateSubmitState, true);

      if (checkbox) {
        checkbox.addEventListener('change', (e) => {
          const presaleState = getPresaleState({
            saleStartUnix,
            nowUnix: Math.floor(Date.now() / 1000),
          });
          if (presaleState.isLocked) {
            presaleController.sync();
            return;
          }
          toggleSelected(!!e.target.checked);
        });
      }

      toggleSelected(!!checkbox?.checked);

      assignItemState(validateSelectedItem);
      presaleController.start();
    }

    items.forEach(setupItem);
    setError('');

    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        setError('');

        const selected = getSelectedItems();
        if (selected.length === 0) {
          setError('商品を選択してください。');
          return;
        }
        selected.forEach((it) => {
          clearFieldErrors(it.querySelector('[data-attendees-grid]'));
        });

        const payloadItems = [];

        for (const it of selected) {
          const itemState = it.__bulkTourState || {};
          const validation = itemState.validateSelectedItem
            ? itemState.validateSelectedItem({ showErrors: true })
            : { ok: false, message: '入力状態の取得に失敗しました。' };
          if (!validation.ok) {
            setError(validation.message || '入力内容を確認してください。');
            return;
          }

          payloadItems.push({
            id: validation.variantId,
            quantity: validation.quantity,
            properties: validation.properties,
          });
        }

        if (payloadItems.length === 0) {
          setError('カートに追加できる商品がありません。');
          return;
        }

        submitBtn.disabled = true;
        submitBtn.classList.add('loading');

        const finishApplicationChange = window.MFCartApplication?.beginChange();
        try {
          const url = (window.routes && window.routes.cart_add_url) ? `${window.routes.cart_add_url}.js` : '/cart/add.js';
          const result = await postBulkOrderItemsWithTimeout({
            fetchImpl: fetch,
            url,
            payloadItems,
            setError,
            onSuccess: async () => {
              const syncModule = window.MFCartAttributesSync;
              if (syncModule && typeof syncModule.syncApplicationDataAttribute === 'function') {
                try {
                  await syncModule.syncApplicationDataAttribute({
                    fetchImpl: fetch,
                    now: () => new Date(),
                    consoleObj: console,
                  });
                } catch (_) {
                  // best-effort: CSV 可視化のための同期失敗はカート遷移を阻害しない
                }
              }
              redirectToCart(window);
            },
            timeoutMs: cartAddTimeoutMs,
            AbortControllerImpl: typeof AbortController === 'function' ? AbortController : null,
            setTimeoutFn: (callback, ms) => window.setTimeout(callback, ms),
            clearTimeoutFn: (id) => window.clearTimeout(id),
            consoleObj: console,
          });
          if (!result.ok) return;
        } finally {
          finishApplicationChange?.();
          submitBtn.classList.remove('loading');
          updateSubmitState();
        }
      });
    }
  }

  function startBulkOrder() {
    document.querySelectorAll('.mf-bulk-order').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startBulkOrder);
  } else {
    startBulkOrder();
  }
})();

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    applyBulkItemPresaleState,
    buildAttendeeRowValidationResult,
    cloneAttendeeTemplateNode,
    createValidateAttendeeRow,
    disableInvalidBulkItemSelection,
    hasBulkItemBaseControls,
    hasBulkTourFormDom,
    postBulkOrderItemsWithTimeout,
    redirectToCart,
    resolveCartUrl,
  };
}
